import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithCredential,
  updateProfile,
  GoogleAuthProvider,
  signOut as fbSignOut,
} from 'firebase/auth';
import type { AuthProvider, User } from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { appleProvider, auth, db, googleProvider } from '../lib/firebase';
import { clearStoredLab, getStoredLab } from '../lib/lab';
import { isNativeShell, postToNative, registerNativeAuthHandlers } from '../lib/nativeBridge';
import { publishUserLookup } from '../services/projects';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  /** Native sign-in is in flight — the OS sheet is open or the token is being exchanged. */
  signingIn: boolean;
  /** Carries the `admin` custom claim. Claims are signed, so the client cannot forge one. */
  isAdmin: boolean;
  /** Google, the account most benches already have open. */
  signIn: () => Promise<void>;
  /** Apple. Required by App Store guideline 4.8 alongside Google. */
  signInWithApple: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * Sign in, preferring a popup on every device.
 *
 * Redirect used to be the mobile-friendly choice, but it depends on the browser
 * sharing storage between this origin and the Firebase auth domain. The app is
 * served from blisxbench.web.app while the handler lives on
 * blisxbench.firebaseapp.com, and mobile browsers now block that cross-site
 * storage — so the user lands back on the sign-in screen, silently signed out.
 *
 * A popup carries its result back through window.opener instead of storage, so
 * it is unaffected. Redirect stays as the fallback for the cases where a popup
 * genuinely cannot open.
 */
function isMobileBrowser() {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

async function startSignIn(provider: AuthProvider) {
  // Mobile browsers frequently open a "popup" as an ordinary tab, which loses
  // window.opener — so the popup result never comes back and the user lands on
  // the sign-in screen again. Redirect is the reliable path there.
  //
  // VITE_FIREBASE_AUTH_DOMAIN must stay blisxbench.web.app — the site's own
  // origin. On a different origin (blisxbench.firebaseapp.com) the handler at
  // /__/auth/handler is third-party storage, which Chrome partitions away: the
  // redirect returns, getRedirectResult yields null, and the user lands back on
  // the sign-in screen with no error anywhere. Two things hold this up, and
  // changing either one alone breaks sign-in completely:
  //
  //   1. https://blisxbench.web.app/__/auth/handler must be an authorised
  //      redirect URI on the Web OAuth client (753912902799-3eqj…), or Google
  //      answers every attempt with "Error 400: redirect_uri_mismatch".
  //      Registered 2026-09-18.
  //   2. navigateFallbackDenylist in vite.config.ts must keep /__/ out of the
  //      service worker. Same-origin means the worker can intercept the handler
  //      and serve the app shell instead, which hangs sign-in on a spinner.
  if (isMobileBrowser()) {
    await signInWithRedirect(auth, provider);
    return;
  }

  try {
    await signInWithPopup(auth, provider);
  } catch (e) {
    const code = (e as { code?: string })?.code ?? '';
    if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') throw e;
    if (code !== 'auth/popup-blocked' && code !== 'auth/operation-not-supported-in-this-environment') {
      throw e;
    }
    await signInWithRedirect(auth, provider);
  }
}

async function ensureUserDoc(user: User) {
  const ref = doc(db, 'users', user.uid);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await setDoc(ref, { lastSeenAt: serverTimestamp() }, { merge: true });
  } else {
    await setDoc(ref, {
      email: user.email,
      displayName: user.displayName,
      createdAt: serverTimestamp(),
      lastSeenAt: serverTimestamp(),
    });
  }

  // A code entered on this device before signing in: join it now, through the
  // server, unless the account already belongs somewhere.
  const lab = getStoredLab();
  if (lab && !snap.data()?.labCode) {
    const { joinLab } = await import('../services/projects');
    await joinLab(user.uid, lab).catch((e) => console.warn('could not join stored lab', e));
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // In the native shell the button hands off to the OS and comes back async,
  // so the spinner has to be driven from here rather than by the popup.
  const [nativePending, setNativePending] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  /**
   * The shell signs in with Google natively — the only place Google permits it
   * — then calls back with an ID token. Exchanging it here yields the same
   * Firebase session a browser popup would have produced.
   */
  useEffect(() => {
    return registerNativeAuthHandlers({
      onIdToken: (idToken) => {
        setNativePending(true);
        signInWithCredential(auth, GoogleAuthProvider.credential(idToken))
          .catch((e) => {
            const code = (e as { code?: string })?.code ?? '';
            setError(
              `${describeAuthError(e)}${code ? ` [${code}]` : ''}`.trim() ||
                'Signing in with that Google account failed.',
            );
          })
          .finally(() => setNativePending(false));
      },
      onAppleCredential: ({ identityToken, rawNonce, displayName }) => {
        setNativePending(true);
        // The raw nonce is what makes this token verifiable; see
        // NativeAppleCredential in lib/nativeBridge.ts.
        signInWithCredential(auth, appleProvider.credential({ idToken: identityToken, rawNonce }))
          .then(async ({ user: signedIn }) => {
            // Apple sends a name only on the first authorisation ever made for
            // this Apple ID. If it is not written now there is no second
            // chance, and the account stays nameless for good.
            if (displayName && !signedIn.displayName) {
              await updateProfile(signedIn, { displayName }).catch(() => {});
            }
          })
          .catch((e) => {
            const code = (e as { code?: string })?.code ?? '';
            setError(
              `${describeAuthError(e)}${code ? ` [${code}]` : ''}`.trim() ||
                'Signing in with that Apple ID failed.',
            );
          })
          .finally(() => setNativePending(false));
      },
      onError: (message) => {
        setNativePending(false);
        // Never clear the spinner without saying why — a screen that simply
        // stops is impossible to report or diagnose.
        setError(message || 'Sign-in did not complete.');
      },
    });
  }, []);

  useEffect(() => {
    // Resolve a pending redirect sign-in before we trust the null state.
    getRedirectResult(auth)
      .catch((e) => {
        const code = (e as { code?: string })?.code ?? '';
        setError(`${describeAuthError(e)}${code ? ` [${code}]` : ''}`.trim());
      })
      .finally(() => setLoading(false));

    return onAuthStateChanged(
      auth,
      (u) => {
        setUser(u);
        setLoading(false);
        if (!u) setIsAdmin(false);
        if (u) {
          // The admin claim rides in the ID token. Read it rather than trusting
          // any Firestore flag, which a client could write to itself.
          // Display only — the server checks the same thing on every admin call.
          u.getIdTokenResult(true)
            .then((t) =>
              setIsAdmin(
                t.claims.email === 'tangjr6@gmail.com' &&
                  t.claims.email_verified === true &&
                  t.signInProvider === 'google.com',
              ),
            )
            .catch(() => setIsAdmin(false));

          // Both writes are best-effort: never let them gate the session.
          ensureUserDoc(u).catch((e) => console.warn('profile write failed', e));
          // Publishing the address is what lets a colleague share a project
          // with this account by typing the email.
          publishUserLookup(u).catch((e) => console.warn('lookup write failed', e));
        }
      },
      (e) => {
        setError(describeAuthError(e));
        setLoading(false);
      },
    );
  }, []);

  /**
   * Hand a sign-in to the shell and wait for it to call back.
   *
   * Both providers need this for the same reason from opposite directions:
   * Google refuses OAuth inside an embedded WebView, and Apple's JS flow wants
   * a real browser window it does not have here. The OS sheet is the only route
   * in either case, and it is the nicer one.
   */
  const handoffToNative = useCallback((type: 'google-signin' | 'apple-signin') => {
    setNativePending(true);
    if (!postToNative({ type })) {
      setNativePending(false);
      setError('The app could not reach native sign-in. Reopen the app and try again.');
      return;
    }
    // If the native side never answers, say so rather than spinning for ever.
    window.setTimeout(() => {
      setNativePending((pending) => {
        if (pending) {
          setError('Sign-in timed out with no response from the app. Please try again.');
        }
        return false;
      });
    }, 90_000);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      error,
      signingIn: nativePending,
      isAdmin,
      async signIn() {
        setError(null);
        if (isNativeShell()) return handoffToNative('google-signin');
        try {
          await startSignIn(googleProvider);
        } catch (e) {
          setError(describeAuthError(e));
        }
      },
      async signInWithApple() {
        setError(null);
        if (isNativeShell()) return handoffToNative('apple-signin');
        try {
          await startSignIn(appleProvider);
        } catch (e) {
          setError(describeAuthError(e));
        }
      },
      async signOut() {
        // Clear the native Google session too, or the next sign-in silently
        // reuses the same account with no chooser.
        if (isNativeShell()) postToNative({ type: 'google-signout' });
        // Forget the device's lab too. The account keeps its own copy, so the
        // next sign-in loses nothing — but on a shared phone the next person
        // would otherwise be offered, and signed into, the last person's lab.
        clearStoredLab();
        await fbSignOut(auth);
      },
    }),
    [user, loading, error, nativePending, isAdmin, handoffToNative],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

/** Turn Firebase's opaque codes into something a solo dev can act on at 2am. */
function describeAuthError(e: unknown): string {
  const code = (e as { code?: string })?.code ?? '';
  switch (code) {
    case 'auth/popup-blocked':
      return 'The sign-in popup was blocked. Allow popups, or reload and try again.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return '';
    case 'auth/unauthorized-domain':
      return 'This domain is not in the Firebase authorised domains list. Add it under Authentication > Settings > Authorised domains.';
    case 'auth/network-request-failed':
      return 'Network unreachable. Sign-in needs a connection, though captured data does not.';
    case 'auth/operation-not-allowed':
      return 'That sign-in method is not enabled for this project yet. Enable it under Authentication > Sign-in method in the Firebase console.';
    case 'auth/invalid-email':
      return 'That does not look like an email address.';
    case 'auth/missing-password':
      return 'Enter your password.';
    case 'auth/weak-password':
      return 'Passwords need to be at least six characters.';
    case 'auth/email-already-in-use':
      return 'There is already an account with that address. Sign in instead, or reset the password.';
    // Firebase collapses wrong-password and no-such-user into one code on
    // purpose, so that a stranger cannot use the sign-in form to find out who
    // has an account. Say the same thing for the older split codes.
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'That email and password do not match an account.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a minute and try again.';
    case 'auth/user-disabled':
      return 'That account has been disabled.';
    default:
      return (e as Error)?.message ?? 'Sign-in failed.';
  }
}
